import { createSlice } from "@reduxjs/toolkit";
import { SPAR_DISTRIBUTOR_I_CUSTOMER } from "../constants/distributors";

// ----------------------------------------------------------------------
// Initial State
// NOTE: redux-persist handles localStorage sync automatically via rootReducer config
// No manual localStorage read/write needed in this slice
// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  /** @type {object|null} Full user object from JWT/login response */
  user: null,
  /** @type {object|null} Currently active role object */
  selectedRole: null,
  /** @type {Array} All available roles for the user */
  availableRoles: [],
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Initialize auth state (called on app load after checking token)
     * @param {object} action.payload - { isAuthenticated: boolean, user: object|null }
     */
    initialize: (state, action) => {
      state.isInitialized = true;
      state.isAuthenticated = action.payload.isAuthenticated;

      if (action.payload.user) {
        state.user = action.payload.user;
        state.availableRoles = action.payload.user.roles || [];
        // Handle new active_role structure or fallback to first role
        state.selectedRole = action.payload.user.activeRole ||
                           (action.payload.user.roles && action.payload.user.roles.length > 0
                             ? action.payload.user.roles[0]
                             : null);
      } else {
        state.user = null;
        state.selectedRole = null;
        state.availableRoles = [];
      }
      // redux-persist handles localStorage automatically
    },

    /**
     * Set auth state after successful login
     * @param {object} action.payload - { user: object }
     */
    login: (state, action) => {
      state.isAuthenticated = true;
      state.isInitialized = true; // Mark as initialized after login

      if (action.payload.user) {
        state.user = action.payload.user;
        state.availableRoles = action.payload.user.roles || [];
        // Handle new active_role structure or fallback to first role
        state.selectedRole = action.payload.user.activeRole ||
                           (action.payload.user.roles && action.payload.user.roles.length > 0
                             ? action.payload.user.roles[0]
                             : null);
      } else {
        state.user = null;
        state.selectedRole = null;
        state.availableRoles = [];
      }
      // redux-persist handles localStorage automatically
    },

    /**
     * Clear all auth state on logout
     */
    logout: (state) => {
      state.isAuthenticated = false;
      state.isInitialized = false;
      state.user = null;
      state.selectedRole = null;
      state.availableRoles = [];
      // redux-persist handles localStorage cleanup automatically
    },

    /**
     * Update selected role (after server-side role switch)
     * @param {object} action.payload - { activeRole: object, roles: Array, user?: object }
     */
    setRole: (state, action) => {
      const { activeRole, roles, user } = action.payload;

      if (activeRole) {
        state.selectedRole = activeRole;
      }

      if (roles && Array.isArray(roles)) {
        state.availableRoles = roles;
      }

      // Optionally update user fields if provided
      if (user && state.user) {
        state.user = { ...state.user, ...user };
      }
      // redux-persist handles localStorage automatically
    },

    /**
     * Update available roles list (from server fetch)
     * @param {Array} action.payload - Array of role objects
     */
    setAvailableRoles: (state, action) => {
      if (Array.isArray(action.payload)) {
        state.availableRoles = action.payload;
      }
    },

    /**
     * Mark current role's details as verified
     */
    setDetailsVerified: (state) => {
      if (state.selectedRole) {
        state.selectedRole.details_verified = true;
      }
      // redux-persist handles localStorage automatically
    },

    /**
     * Set details_verified to explicit value
     * @param {boolean} action.payload - true/false
     */
    setDetailsVerifiedValue: (state, action) => {
      if (state.selectedRole) {
        state.selectedRole.details_verified = action.payload === true; // force boolean
      }
      // redux-persist handles localStorage automatically
    },

    /**
     * Update user profile fields
     * @param {object} action.payload - Partial user object to merge
     */
    updateUser: (state, action) => {
      if (state.user && action.payload) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
});

export const {
  initialize,
  login,
  logout,
  setRole,
  setAvailableRoles,
  setDetailsVerified,
  setDetailsVerifiedValue,
  updateUser,
} = authSlice.actions;

// ----------------------------------------------------------------------
// Selectors
// Use these in components instead of directly accessing state or decoding JWT
// ----------------------------------------------------------------------

/**
 * Select the currently active role object
 * @param {object} state - Redux state
 * @returns {object|null} Current role object or null
 */
export const selectCurrentRole = (state) => state.auth.selectedRole;

/**
 * Select all available roles for the user
 * @param {object} state - Redux state
 * @returns {Array} Array of role objects
 */
export const selectAvailableRoles = (state) => state.auth.availableRoles;

/**
 * Select the full user object
 * @param {object} state - Redux state
 * @returns {object|null} User object or null
 */
export const selectUser = (state) => state.auth.user;

/**
 * Select authentication status
 * @param {object} state - Redux state
 * @returns {boolean} Whether user is authenticated
 */
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;

/**
 * Select initialization status
 * @param {object} state - Redux state
 * @returns {boolean} Whether auth has been initialized
 */
export const selectIsInitialized = (state) => state.auth.isInitialized;

/**
 * Select current role type string (e.g., 'admin', 'distributor')
 * @param {object} state - Redux state
 * @returns {string|null} Role type string or null
 */
export const selectCurrentRoleType = (state) => state.auth.selectedRole?.role || null;

/**
 * Select current role_id
 * @param {object} state - Redux state
 * @returns {string|number|null} Role ID or null
 */
export const selectCurrentRoleId = (state) => state.auth.selectedRole?.role_id || null;

/**
 * Select username from user object
 * @param {object} state - Redux state
 * @returns {string|null} Username or null
 */
export const selectUsername = (state) => state.auth.user?.username || null;

/**
 * Select external_id from user object
 * @param {object} state - Redux state
 * @returns {string|null} External ID or null
 */
export const selectExternalId = (state) => state.auth.user?.external_id || null;

/**
 * Check if user has a specific role type
 * @param {string} roleType - Role type to check (e.g., 'admin')
 * @returns {function} Selector function
 */
export const selectHasRoleType = (roleType) => (state) =>
  state.auth.availableRoles.some(role => role.role === roleType);

/**
 * Check if user has SPAR distributor role (distributor_i_customer === 26215)
 * @param {object} state - Redux state
 * @returns {boolean} Whether user has SPAR role
 */
export const selectHasSparRole = (state) =>
  state.auth.availableRoles.some(role => role.distributor_i_customer === SPAR_DISTRIBUTOR_I_CUSTOMER);

export default authSlice.reducer;
